<template>
  <el-form @submit.prevent>
    <div class="inputs-wrap">
      <el-row :gutter="20">
        <el-col :xl="7" :lg="8" :md="14">
          <el-form-item :label="t('fields.creditFlowDate') + ' :'">
            <el-date-picker
              v-model="request.date"
              format="DD/MM/YYYY"
              value-format="YYYY-MM-DD"
              size="normal"
              type="daterange"
              range-separator=":"
              :shortcuts="shortcuts"
              :start-placeholder="t('fields.startDate')"
              :end-placeholder="t('fields.endDate')"
              :disabled-date="disabledDate"
              :default-time="defaultTime"
              style="width: 100%;"
              :clearable="false"
              :editable="false"
            />
          </el-form-item>
        </el-col>
        <el-col :xl="8" :lg="8">
          <div class="btn-grp">
            <el-button
              icon="el-icon-search"
              type="primary"
              @click="loadCreditFlow()"
              size="normal"
            >
              {{ $t('fields.search') }}
            </el-button>
            <el-button size="normal" type="primary" plain @click="resetQuery()">
              {{ $t('fields.reset') }}
            </el-button>
          </div>
        </el-col>
      </el-row>
    </div>
  </el-form>

  <table
    class="custom-table"
    style="margin: 10px auto; width: 100%;"
    cellpadding="0"
    cellspacing="0"
    border="0"
  >
    <thead>
      <tr>
        <th scope="col">{{ t('fields.creditFlowDate') }}</th>
        <th scope="col">{{ t('fields.serialNumber') }}</th>
        <th scope="col">{{ t('fields.creditFlowType') }}</th>
        <th scope="col">{{ t('fields.creditFlowSubType') }}</th>
        <th scope="col">{{ t('fields.creditFlowAmount') }}</th>
        <th scope="col">{{ t('fields.beforeBalance') }}</th>
        <th scope="col">{{ t('fields.afterBalance') }}</th>
        <th scope="col">{{ t('fields.targetName') }}</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="row in page.records" :key="row.id">
        <td data-label="Date">
          <span v-if="row.recordTime === null">-</span>
          <span v-if="row.recordTime !== null">
            {{ moment(row.recordTime).format('YYYY/MM/DD HH:mm:ss') }}
          </span>
        </td>
        <td date-label="Serial Number">{{ row.serialNumber }}</td>
        <td date-label="Money Change Type">
          {{ row.type === 'TRANSFER' || row.type === 'AFFILIATE_BET_REBATE' ? (row.beforeBalance > row.afterBalance ? $t('fields.transferOut') : $t('fields.transferIn'))
            : $t(`moneyChangeType.${row.type}`) }}
        </td>
        <td date-label="Money Change Type">
          {{ row.subType === null ? '-' : $t(`moneyChangeSubType.${row.subType}`) }}
        </td>
        <td date-label="Amount">
          <span v-formatter="{data: row.amount, type: 'money'}" />
        </td>
        <td date-label="Before Balance">
          <span v-formatter="{data: row.beforeBalance, type: 'money'}" />
        </td>
        <td date-label="After Balance">
          <span v-formatter="{data: row.afterBalance, type: 'money'}" />
        </td>
        <td date-label="Target Name">{{ row.platformCode }}</td>
      </tr>
    </tbody>
  </table>
  <div v-if="page.records.length === 0">
    <emptyComp />
  </div>
  <el-pagination
    class="pagination"
    @current-change="changePage"
    layout="prev, pager, next"
    :page-size="request.size"
    :page-count="page.pages"
    :current-page="request.current"
  />
</template>

<script setup>
import { reactive, onMounted } from 'vue'
import { useStore } from '@/store'
import { useI18n } from 'vue-i18n'

import { getMoneyChange } from '../../../../api/affiliate-credit-flow'
import emptyComp from '@/components/empty'
import moment from 'moment'

const store = useStore()
const { t } = useI18n()

const request = reactive({
  siteId: null,
  affiliateId: null,
  category: null,
  type: null,
  date: [convertStartDate(new Date()), convertDate(new Date())],
  size: 20,
  current: 1,
})

const page = reactive({
  pages: 0,
  records: [],
  loading: false,
})

const shortcuts = [
  {
    text: t('fields.today'),
    value: () => {
      const end = new Date()
      const start = new Date()
      return [start, end]
    },
  },
  {
    text: t('fields.yesterday'),
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(
        moment(start)
          .subtract(1, 'days')
          .format('x')
      )
      end.setTime(
        moment(end)
          .subtract(1, 'days')
          .format('x')
      )
      return [start, end]
    },
  },
  {
    text: t('fields.thisWeek'),
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(
        moment(start)
          .startOf('isoWeek')
          .format('x')
      )
      return [start, end]
    },
  },
  {
    text: t('fields.lastWeek'),
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(
        moment(start)
          .subtract(1, 'weeks')
          .startOf('isoWeek')
          .format('x')
      )
      end.setTime(
        moment(end)
          .subtract(1, 'weeks')
          .endOf('isoWeek')
          .format('x')
      )
      return [start, end]
    },
  },
  {
    text: t('fields.thisMonth'),
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(
        moment(start)
          .startOf('month')
          .format('x')
      )
      return [start, end]
    },
  },
  {
    text: t('fields.lastMonth'),
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(
        moment(start)
          .subtract(1, 'months')
          .startOf('month')
          .format('x')
      )
      end.setTime(
        moment(end)
          .subtract(1, 'months')
          .endOf('month')
          .format('x')
      )
      return [start, end]
    },
  },
]

async function loadCreditFlow() {
  const requestCopy = { ...request }
  const query = {}
  Object.entries(requestCopy).forEach(([key, value]) => {
    if (value) {
      query[key] = value
    }
  })

  if (request.date != null) {
    if (request.date.length === 2) {
      query.date = request.date.join(',')
    }
  } else {
    query.date = null
  }

  query.affiliateId = store.state.user.id
  query.siteId = store.state.user.siteId
  query.category = 'COMMISSION'
  query.size = 20

  const { data: ret } = await getMoneyChange(query)

  page.pages = ret.pages
  page.records = ret.records

  page.loading = false
}

function resetQuery() {
  request.date = [convertStartDate(new Date()), convertDate(new Date())]
  loadCreditFlow()
}

function changePage(page) {
  if (request.current >= 1) {
    request.current = page
    loadCreditFlow()
  }
}

function convertStartDate(date) {
  return moment(date)
    .startOf('day')
    .format('YYYY-MM-DD')
}

function convertDate(date) {
  return moment(date).format('YYYY-MM-DD')
}

onMounted(() => {
  loadCreditFlow()
})
</script>
<style rel="stylesheet/scss" lang="scss" scoped>
.grid-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.grid-item {
  height: 110px;
  position: relative;
  border: 1px solid #f2f2f6;
  border-radius: 8px;
  padding: 20px;
}

.grid-item-label {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 25px;

  span {
    font-size: 16px;
    font-weight: 600;
    color: #afb3c8;
  }
}

.grid-item-value {
  font-size: 16px;
  font-weight: 700;
}
</style>
